import React, { FC, useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  UpsertQuestionAnswerDto,
  StakeholderPublicInfoDto,
  QuestionAnswerRowDto,
  CompanyPublicInfoDto,
  DoubleMaterialityESRSAreaEnum,
  DoubleMaterialityIroTypeEnum,
  DoubleMaterialityStakeholderTypeEnum,
} from "@green0meter/api-client";
import axios from "axios";
import DOMPurify from "dompurify";

import { DMSurveySuccess } from "./DMSurveySuccess";
import { DMSurveyIntroduction } from "./DMSurveyIntroduction";
import { ImpactAssessment } from "./ImpactAssessment";
import { RiskAssessment } from "./RiskAssessment";
import { OpportunityAssessment } from "./OpportunityAssessment";
import { getFullLogoUrl } from "./DMSurveyLogo";

import RichTextBox from "@components/common/RichTextBox/RichTextBox";
import { useESRSLabels } from "@components/main/csrd/DoubleMateriality/components/Stakeholders/components/useESRSLabels";
import { doubleMaterialityQuestionAnswersApi } from "@api/services";
import { DmRangeKey } from "@constants/dmRanges";

interface QuestionData {
  sustainabilityMatter?: string;
  questions?: Record<string, string> | string;
  descriptions?: Record<string, string> | string;
  textFieldLabels?: Record<string, string> | null;
  assessment?: {
    assessmentId?: string;
    sustainabilityMatter?: string;
    topic?: DoubleMaterialityESRSAreaEnum;
  };
  iro: DoubleMaterialityIroTypeEnum | null;
  stakeholderType: DoubleMaterialityStakeholderTypeEnum | null;
  activateTextField?: boolean;
}

interface QuestionWithAnswer extends QuestionAnswerRowDto {
  year: number;
  id: string;
  question?: QuestionData;
}

export interface ImpactAnswers {
  /** actual/potential */
  isReal?: boolean | null;
  /** positive/negative */
  isPositiveImpact?: boolean | null;
  /** likelihood (1-5) */
  probability?: number | null;
  /** intensity (1-5) */
  intensity?: number | null;
  /** scope (1-5) */
  range?: number | null;
  /** irremediable character (1-5) */
  remediability?: number | null;
  /** short=0, medium=1, long=2 */
  timeHorizon?: number[] | null;
}

export interface RiskAnswers {
  /** likelihood (1-5) */
  probability?: number | null;
  /** magnitude (1-5) */
  levelOfSeverity?: number | null;
  /** short=0, medium=1, long=2 */
  timeHorizon?: number[] | null;
}

export interface OpportunityAnswers {
  /** likelihood (1-5) */
  probability?: number | null;
  /** magnitude (1-5) */
  levelOfSeverity?: number | null;
  /** short=0, medium=1, long=2 */
  timeHorizon?: number[] | null;
}

interface AnswerState {
  impact?: ImpactAnswers;
  risk?: RiskAnswers;
  opportunity?: OpportunityAnswers;
  textAnswer?: string;
}

interface DMSurveyExternalProps {
  stakeholderId: string;
  stakeholderInfo: StakeholderPublicInfoDto;
  questions: QuestionWithAnswer[];
  onSubmitSuccess: () => void;
  surveyRanges: Record<DmRangeKey, string[]>;
}

export const DMSurveyInternal: FC<DMSurveyExternalProps> = ({
  stakeholderId,
  stakeholderInfo,
  questions,
  onSubmitSuccess,
  surveyRanges,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const { getEsrsLabelFromEnum } = useESRSLabels();

  const deadlinePassed = stakeholderInfo.deadline
    ? new Date(stakeholderInfo.deadline) < new Date()
    : false;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyPublicInfoDto | null>(null);
  const [stakeholderComment, setStakeholderComment] = useState<string>(
    stakeholderInfo.surveyComment || "",
  );
  const autoSaveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const commentAutoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const questionTitleRef = useRef<HTMLDivElement>(null);

  // Fetch company info
  useEffect(() => {
    const fetchCompanyInfo = async (): Promise<void> => {
      if (!stakeholderInfo?.surveyCompanyId) return;

      const response = await axios.get<CompanyPublicInfoDto>(
        `/api/companies/public/${stakeholderInfo.surveyCompanyId}`,
      );

      setCompanyInfo(response.data);
    };

    fetchCompanyInfo();
  }, [stakeholderInfo.surveyCompanyId]);

  const currentQuestion = questions[currentQuestionIndex];
  const currentQuestionId = currentQuestion?.questionId || "";
  const currentAnswer = answers[currentQuestionId] || {};

  const hasIro = (iro: DoubleMaterialityIroTypeEnum): boolean => {
    return currentQuestion?.question?.iro === iro;
  };

  const questionHasIro = (
    question: QuestionWithAnswer,
    iro: DoubleMaterialityIroTypeEnum,
  ): boolean => {
    return question?.question?.iro === iro;
  };

  const canAssessIro = (iro: DoubleMaterialityIroTypeEnum): boolean => {
    // If no impact areas assigned, allow all types (backward compatibility)
    if (!stakeholderInfo.impactAreas || stakeholderInfo.impactAreas.length === 0) {
      return true;
    }

    // Map IRO enum to stakeholder impact area values
    const iroTypeMap: Record<DoubleMaterialityIroTypeEnum, string> = {
      [DoubleMaterialityIroTypeEnum.Impact]: "impact",
      [DoubleMaterialityIroTypeEnum.Risk]: "risk",
      [DoubleMaterialityIroTypeEnum.Opportunity]: "opportunity",
    };

    const iroValue = iroTypeMap[iro];

    return stakeholderInfo.impactAreas.some(
      (area) => area.toLowerCase() === iroValue,
    );
  };

  const getTextFieldLabel = (questionData?: QuestionData): string => {
    const lang = i18n.language?.split("-")[0] ?? "en";
    const labels = questionData?.textFieldLabels;

    if (labels && typeof labels === "object" && Object.keys(labels).length > 0) {
      // Try stakeholder's language first, then current UI language, then English, then first available
      const stakeholderLang = String(stakeholderInfo.language).toLowerCase() || "en";
      const label =
        labels[stakeholderLang] ||
        labels[lang] ||
        labels["en"] ||
        Object.values(labels)[0];

      if (label) return label;
    }

    // Fallback to default translation
    return t(
      "dmSurvey.questionComment",
      "Additional comments for this question (optional)",
    );
  };

  useEffect(() => {
    if (!questions?.length) return;

    setAnswers((prevAnswers) => {
      const newAnswers = { ...prevAnswers };
      let hasChanges = false;

      const pickValues = (
        source: QuestionWithAnswer,
        keys: (keyof QuestionWithAnswer)[],
      ): Record<string, unknown> => {
        return keys.reduce<Record<string, unknown>>((acc, key) => {
          const value = source[key];

          if (value !== null && value !== undefined) {
            acc[key as string] = Array.isArray(value) ? [...value] : value;
          }

          return acc;
        }, {});
      };

      questions.forEach((question) => {
        const questionId = question.questionId;

        if (!questionId) return;

        // If we already have an answer state for this question, keep it via preserving prevAnswers content.
        if (newAnswers[questionId]) return;

        const answerState: AnswerState = {};

        answerState.textAnswer = question.textAnswer ?? "";

        if (questionHasIro(question, DoubleMaterialityIroTypeEnum.Impact)) {
          const impact = pickValues(question, [
            "isReal",
            "isPositiveImpact",
            "probability",
            "intensity",
            "range",
            "remediability",
            "timeHorizon",
          ]) as ImpactAnswers;

          if (Object.keys(impact).length > 0) {
            answerState.impact = impact;
          }
        }

        if (questionHasIro(question, DoubleMaterialityIroTypeEnum.Risk)) {
          const risk = pickValues(question, [
            "probability",
            "levelOfSeverity",
            "timeHorizon",
          ]) as RiskAnswers;

          if (Object.keys(risk).length > 0) {
            answerState.risk = risk;
          }
        }

        if (questionHasIro(question, DoubleMaterialityIroTypeEnum.Opportunity)) {
          const opportunity = pickValues(question, [
            "probability",
            "levelOfSeverity",
            "timeHorizon",
          ]) as OpportunityAnswers;

          if (Object.keys(opportunity).length > 0) {
            answerState.opportunity = opportunity;
          }
        }

        newAnswers[questionId] = answerState;
        hasChanges = true;
      });

      return hasChanges ? newAnswers : prevAnswers;
    });
  }, [questions]);

  // Validation: Check if a question is fully answered
  const isQuestionComplete = (question: QuestionWithAnswer): boolean => {
    const questionId = question.questionId;

    if (!questionId) return false;

    const answer = answers[questionId];

    if (!answer) return false;

    // Check Impact section if present AND stakeholder can assess it
    if (
      questionHasIro(question, DoubleMaterialityIroTypeEnum.Impact) &&
      canAssessIro(DoubleMaterialityIroTypeEnum.Impact)
    ) {
      if (!answer.impact) return false;

      const impact = answer.impact;
      const requiresLikelihood = impact.isReal === false;
      const requiresRemediability = impact.isPositiveImpact === false;

      if (
        impact.isReal === null ||
        impact.isReal === undefined ||
        impact.isPositiveImpact === null ||
        impact.isPositiveImpact === undefined ||
        impact.intensity === null ||
        impact.intensity === undefined ||
        impact.range === null ||
        impact.range === undefined ||
        !impact.timeHorizon ||
        impact.timeHorizon.length === 0 ||
        (requiresLikelihood &&
          (impact.probability === null || impact.probability === undefined)) ||
        (requiresRemediability &&
          (impact.remediability === null || impact.remediability === undefined))
      ) {
        return false;
      }
    }

    // Check Risk section if present AND stakeholder can assess it
    if (
      questionHasIro(question, DoubleMaterialityIroTypeEnum.Risk) &&
      canAssessIro(DoubleMaterialityIroTypeEnum.Risk)
    ) {
      if (!answer.risk) return false;

      const risk = answer.risk;

      if (
        !risk.probability ||
        !risk.levelOfSeverity ||
        !risk.timeHorizon ||
        risk.timeHorizon.length === 0
      ) {
        return false;
      }
    }

    // Check Opportunity section if present AND stakeholder can assess it
    if (
      questionHasIro(question, DoubleMaterialityIroTypeEnum.Opportunity) &&
      canAssessIro(DoubleMaterialityIroTypeEnum.Opportunity)
    ) {
      if (!answer.opportunity) return false;

      const opportunity = answer.opportunity;

      if (
        !opportunity.probability ||
        !opportunity.levelOfSeverity ||
        !opportunity.timeHorizon ||
        opportunity.timeHorizon.length === 0
      ) {
        return false;
      }
    }

    return true;
  };

  // Check if all questions are complete
  const isFormComplete = (): boolean => {
    return questions.every((question) => isQuestionComplete(question));
  };

  // Get list of incomplete questions with their numbers
  const getIncompleteQuestions = (): number[] => {
    return questions
      .map((question, index) => ({
        index: index + 1,
        complete: isQuestionComplete(question),
      }))
      .filter((q) => !q.complete)
      .map((q) => q.index);
  };

  // Get tooltip message for incomplete form
  const getIncompleteTooltipMessage = (): string => {
    const incompleteQuestions = getIncompleteQuestions();

    if (incompleteQuestions.length === 0) {
      return "";
    }

    const questionNumbers = incompleteQuestions.join(", ");

    return t(
      "dmSurvey.incompleteQuestionsTooltip",
      "Please complete all questions before submitting. Missing answers in question(s): {{questionNumbers}}",
      { questionNumbers },
    );
  };

  // Auto-save answer with debouncing
  const autoSaveAnswer = useCallback(
    async (questionId: string, answerData: AnswerState): Promise<void> => {
      if (!stakeholderId) return;

      const question = questions.find((q) => q.questionId === questionId);

      if (!question) return;

      // Flatten the answer data for the API
      // Note: Using Record<string, unknown> until API client is regenerated with isPositiveImpact field
      // Helper to convert null to undefined in spread objects
      type NullToUndefined<T> = {
        [K in keyof T]: T[K] extends null | infer U ? U | undefined : T[K];
      };

      const nullToUndefined = <T,>(
        obj: T | undefined,
      ): NullToUndefined<T> | undefined => {
        if (!obj) return undefined;

        return Object.fromEntries(
          Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
            key,
            value === null ? undefined : value,
          ]),
        ) as NullToUndefined<T>;
      };

      const flatAnswer: UpsertQuestionAnswerDto & Record<string, unknown> = {
        questionId,
        stakeholderId,
        year: question.year,
        textAnswer: answerData.textAnswer || undefined,
        // Spread fields from impact, risk, and opportunity assessments
        ...nullToUndefined(answerData.impact),
        ...nullToUndefined(answerData.risk),
        ...nullToUndefined(answerData.opportunity),
      };

      await doubleMaterialityQuestionAnswersApi.doubleMaterialityQuestionAnswersControllerUpsert(
        flatAnswer,
      );
    },
    [stakeholderId, questions],
  );

  const updateAnswer = (
    section: "impact" | "risk" | "opportunity",
    field: string,
    value: unknown,
  ): void => {
    const newAnswers = {
      ...answers,
      [currentQuestionId]: {
        ...currentAnswer,
        [section]: {
          ...currentAnswer[section],
          [field]: value,
        },
      },
    };

    setAnswers(newAnswers);

    // Debounced auto-save
    if (autoSaveTimeoutRef.current[currentQuestionId]) {
      clearTimeout(autoSaveTimeoutRef.current[currentQuestionId]);
    }

    autoSaveTimeoutRef.current[currentQuestionId] = setTimeout(() => {
      autoSaveAnswer(currentQuestionId, newAnswers[currentQuestionId]);
    }, 1000);
  };

  const updateTextAnswer = (value: string): void => {
    const newAnswers = {
      ...answers,
      [currentQuestionId]: {
        ...currentAnswer,
        textAnswer: value,
      },
    };

    setAnswers(newAnswers);

    // Debounced auto-save
    if (autoSaveTimeoutRef.current[currentQuestionId]) {
      clearTimeout(autoSaveTimeoutRef.current[currentQuestionId]);
    }

    autoSaveTimeoutRef.current[currentQuestionId] = setTimeout(() => {
      autoSaveAnswer(currentQuestionId, newAnswers[currentQuestionId]);
    }, 1000);
  };

  const handleStakeholderCommentChange = (comment: string): void => {
    setStakeholderComment(comment);

    if (commentAutoSaveTimeoutRef.current) {
      clearTimeout(commentAutoSaveTimeoutRef.current);
    }

    commentAutoSaveTimeoutRef.current = setTimeout(async () => {
      await axios.put(`/api/dm/stakeholders/public/${stakeholderId}/comment`, {
        comment,
      });
    }, 1000);
  };

  const handlePrevious = (): void => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNext = (): void => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setTimeout(() => {
        const element = questionTitleRef.current;

        if (element) {
          const yOffset = -100;
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;

          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      setSubmitting(true);

      // Clear any pending auto-saves
      Object.values(autoSaveTimeoutRef.current).forEach((timeout) =>
        clearTimeout(timeout),
      );
      autoSaveTimeoutRef.current = {};

      // Save all answers immediately
      await Promise.all(
        questions.map((q) => {
          if (q.questionId && answers[q.questionId]) {
            return autoSaveAnswer(q.questionId, answers[q.questionId]);
          }

          return Promise.resolve();
        }),
      );

      // Mark survey as submitted
      await axios.post(`/api/dm/stakeholders/public/${stakeholderId}/submit`);

      enqueueSnackbar(
        t("dmSurvey.submitSuccess", "Survey submitted successfully!"),
        {
          variant: "success",
        },
      );

      onSubmitSuccess();
    } catch {
      enqueueSnackbar(
        t("dmSurvey.submitError", "Failed to submit survey. Please try again."),
        { variant: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (stakeholderInfo.submittedAt) {
    return <DMSurveySuccess submittedAt={stakeholderInfo.submittedAt} />;
  }

  // fallback to en should never happen
  const currentLang = i18n.language?.split("-")[0] ?? "en";
  const questionText =
    typeof currentQuestion.question?.questions === "object"
      ? currentQuestion.question.questions[currentLang] ||
        currentQuestion.question.questions?.en ||
        currentQuestion.question.questions?.cs ||
        ""
      : currentQuestion.question?.questions || "";

  const descriptionText =
    typeof currentQuestion.question?.descriptions === "object"
      ? currentQuestion.question.descriptions[currentLang] ||
        currentQuestion.question.descriptions?.en ||
        currentQuestion.question.descriptions?.cs ||
        ""
      : currentQuestion.question?.descriptions || "";

  const esrsLabel =
    currentQuestion.question?.assessment?.topic !== undefined
      ? getEsrsLabelFromEnum(currentQuestion.question.assessment.topic)
      : "Uncategorized";

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const getQuestionCardBgColor = (): string => {
    if (hasIro(DoubleMaterialityIroTypeEnum.Impact)) return "rgba(240, 248, 255, 1)";
    if (hasIro(DoubleMaterialityIroTypeEnum.Risk)) return "rgba(255, 244, 228, 0.5)";
    if (hasIro(DoubleMaterialityIroTypeEnum.Opportunity))
      return "rgba(226, 244, 242, 0.5))";

    return "background.paper";
  };
  const innerBackgroundColors = {
    impact: "rgba(187, 222, 251, 0.5)",
    risk: "rgba(255, 226, 187, 0.5)",
    opportunity: "rgba(219, 241, 236, 1)",
  };

  const companyLogoUrl = useMemo(
    () => getFullLogoUrl(companyInfo?.logo, stakeholderInfo?.surveyCompanyId),
    [companyInfo?.logo, stakeholderInfo?.surveyCompanyId],
  );

  return (
    <Box sx={{ bgcolor: "rgba(248, 248, 248, 1)", minHeight: "100vh", height: "100vh", overflow: "auto", py: 4 }}>
      <Container maxWidth="md">
        <Stack spacing={4}>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, bgcolor: "background.paper" }}>
            <DMSurveyIntroduction
              companyName={companyInfo?.name}
              companyLogo={companyLogoUrl}
              deadline={stakeholderInfo.deadline}
            />
          </Paper>

          {/* Question Card */}
          <Paper elevation={1} sx={{ p: 3, bgcolor: getQuestionCardBgColor() }}>
            <Stack spacing={3}>
              {/* Question Title */}
              <Box ref={questionTitleRef}>
                <Typography variant="h6" component="h2" fontWeight="normal" mb={1}>
                  {esrsLabel}
                  {" - "}
                  {currentQuestion.question?.assessment?.sustainabilityMatter ||
                    t("dmSurvey.generalTopic", "General Topic")}
                </Typography>
                {hasIro(DoubleMaterialityIroTypeEnum.Risk) && (
                  <Typography variant="h6" fontWeight="normal" gutterBottom>
                    {t("dmSurvey.riskAssessment", "Financial risk assessment")}
                  </Typography>
                )}
                {hasIro(DoubleMaterialityIroTypeEnum.Opportunity) && (
                  <Typography variant="h6" fontWeight="normal" gutterBottom>
                    {t(
                      "dmSurvey.opportunityAssessment",
                      "Financial opportunity assessment",
                    )}
                  </Typography>
                )}
                {hasIro(DoubleMaterialityIroTypeEnum.Impact) && (
                  <Typography variant="h6" fontWeight="normal" gutterBottom>
                    {t("dmSurvey.impactAssessment", "Financial impact assessment")}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                  component="div"
                >
                  {currentQuestion.question?.assessment?.assessmentId &&
                    `[${currentQuestion.question.assessment.assessmentId}] `}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(questionText),
                    }}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(descriptionText),
                  }}
                />
              </Box>

              {/* Impact Assessment */}
              {hasIro(DoubleMaterialityIroTypeEnum.Impact) &&
                canAssessIro(DoubleMaterialityIroTypeEnum.Impact) && (
                  <ImpactAssessment
                    currentAnswer={currentAnswer.impact}
                    onUpdate={(field, value) => updateAnswer("impact", field, value)}
                    bgColor={innerBackgroundColors.impact}
                    ranges={{
                      likelihood: surveyRanges.likelihood,
                      intensity: surveyRanges.intensity,
                      scope: surveyRanges.scope,
                      remediability: surveyRanges.remediability,
                      timeHorizon: surveyRanges.timeHorizon,
                    }}
                  />
                )}

              {/* Risk Assessment */}
              {hasIro(DoubleMaterialityIroTypeEnum.Risk) &&
                canAssessIro(DoubleMaterialityIroTypeEnum.Risk) && (
                  <RiskAssessment
                    currentAnswer={currentAnswer.risk}
                    onUpdate={(field, value) => updateAnswer("risk", field, value)}
                    bgColor={innerBackgroundColors.risk}
                    ranges={{
                      likelihood: surveyRanges.likelihood,
                      severity: surveyRanges.scale,
                      timeHorizon: surveyRanges.timeHorizon,
                    }}
                  />
                )}

              {/* Opportunity Assessment */}
              {hasIro(DoubleMaterialityIroTypeEnum.Opportunity) &&
                canAssessIro(DoubleMaterialityIroTypeEnum.Opportunity) && (
                  <OpportunityAssessment
                    currentAnswer={currentAnswer.opportunity}
                    onUpdate={(field, value) =>
                      updateAnswer("opportunity", field, value)
                    }
                    bgColor={innerBackgroundColors.opportunity}
                    ranges={{
                      likelihood: surveyRanges.likelihood,
                      severity: surveyRanges.scale,
                      timeHorizon: surveyRanges.timeHorizon,
                    }}
                  />
                )}

              {/* Question-specific text field - shown when activateTextField is true */}
              {currentQuestion.question?.activateTextField && (
                <Box
                  sx={{
                    bgcolor: "rgba(0, 0, 0, 0.05)",
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="normal"
                    sx={{ mb: 2 }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        getTextFieldLabel(currentQuestion.question),
                      ),
                    }}
                  />
                  <RichTextBox
                    key={`text-answer-${currentQuestionId}`}
                    placeholder={t(
                      "dmSurvey.textPlaceholder",
                      "Write your answer here",
                    )}
                    value={currentAnswer.textAnswer || ""}
                    onChange={(value) => updateTextAnswer(value)}
                  />
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Stakeholder general comment - shown only on the last question */}
          {isLastQuestion && (
            <Paper elevation={1} sx={{ p: 3, bgcolor: "background.paper" }}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight="normal">
                  {t(
                    "dmSurvey.additionalCommentsSection",
                    "Additional comments or suggestions",
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "dmSurvey.shareOpinion",
                    "Here you can share your opinions, experiences, or suggestions regarding our approach to sustainability.",
                  )}
                </Typography>
                <RichTextBox
                  placeholder={t(
                    "dmSurvey.textPlaceholder",
                    "Write your answer here",
                  )}
                  value={stakeholderComment}
                  onChange={handleStakeholderCommentChange}
                />
              </Stack>
            </Paper>
          )}

          {/* Navigation */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <IconButton
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              sx={{ visibility: currentQuestionIndex === 0 ? "hidden" : "visible" }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {t("dmSurvey.questionProgress", "Question {{current}} of {{total}}", {
                current: currentQuestionIndex + 1,
                total: questions.length,
              })}
            </Typography>

            {!isLastQuestion && (
              <IconButton
                onClick={handleNext}
                disabled={currentQuestionIndex >= questions.length - 1}
              >
                <ArrowForward />
              </IconButton>
            )}
          </Stack>
          {isLastQuestion && !deadlinePassed && (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <Tooltip
                title={!isFormComplete() ? getIncompleteTooltipMessage() : ""}
                arrow
                placement="top"
              >
                <span style={{ width: "97%" }}>
                  <Button
                    size="large"
                    onClick={handleSubmit}
                    disabled={submitting || !isFormComplete()}
                    sx={{ minWidth: 200, width: "100%" }}
                  >
                    {submitting
                      ? t("dmSurvey.submitting", "Submitting...")
                      : t("dmSurvey.submitSurvey", "Submit")}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};
